import { AutoComplete, Button, CIInfoIcon, CILabel, Input, Loader, Modal, Textarea, accessToken } from 'ccs-ui-components';
import { debounce } from 'lodash';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import axios from 'axios';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import FileUploadButton from '../../../../../../_components/file-upload/FileUploadButton';
import { AUTOCOMPLETE_FIELD, DEBOUNCE_DELAY, HELP_CENTER_CHATPAGE_SERVICE_BASE_URL, OPERATION_VERB, SERVICE_MODE, STATUS_TYPES, VER, associationEventType } from '../../../../../../constants';
import { useEgressFormValidation } from '../../../../../../hooks';
import { IEgressEventMappingModel, IEgressFormValidationModel, IEvent } from '../../../../../../models/index';
import { AppDispatch, useSelector } from '../../../../../../store/AppStore';
import { fetchEventRequest } from '../../../../../../store/actions/sagaActions/events/EventNameSagaActions';
import { fetchServiceEventRequest } from '../../../../../../store/actions/sagaActions/services/IngressServiceEventSagaActions';
import { IActiveServiceEvent } from '../../../../../../store/actions/types/services/GetAllServiceEventTypes';
import { addEgressEventMapping, updateEgressEventMapping } from '../../../../../../store/reducer/services/EgressEventMappingReducer';
import { callGetServiceAssociatedEventApi } from '../../../../../../store/sagas/services/FetchServiceEventSaga';
import { getSchemaForEvent } from '../../../../../../utilities/services/ServiceDetailsUtils';
import { checkEgressForEditMode, egressErrorMessageHandler, isEgressErrorCondition, isEgressEventError, isEgressEventExist, isErrorCondition, isErrorIngressCondition, isIngressExistForEgress } from '../../../../../../utilities/services/ServiceEventAssociationUtils';
import { getModeFromUrl } from '../../../../../../utilities/services/ServiceListUtils';
import { JoltConfigurations } from '../../../../../jolt-mapping-configuration/JoltMappingConfiguration';
import EventSchemaTree from '../association-event-schema/AssociationEventSchema';
import { INGRESS_EVENT_FORM_ACTIONS, getIngressEventSearchListStateReducer, searchListInitialState } from '../service-event-state-manager/ServiceEventStateManager';
import classes from './EgressEventAssociationForm.module.scss';

interface IEgressEventAssociationDetailsProps {
  selectionForEdit: string | null;
  versionForEdit: string | null;
  onCancelButtonClicked: () => void;
  editEgressEventButtonClick: boolean;
  onSaveEventButtonClick: () => void;
  serviceOperationName: string;
  ingressName: string;
  ingressEventVersion: string;
  viewMode?: boolean;
  addBtnClick?: boolean;
  operationVerb: string;
}

const defaultProps = {
  viewMode: false,
  addBtnClick: false
};

interface IEgressDependencyFields {
  topic: string;
  partition: string;
  connection: string;
}

const EgressEventAssociationForm = (
  {
    selectionForEdit,
    versionForEdit,
    onCancelButtonClicked,
    onSaveEventButtonClick,
    editEgressEventButtonClick,
    serviceOperationName,
    ingressName,
    ingressEventVersion,
    viewMode,
    addBtnClick,
    operationVerb
  }: IEgressEventAssociationDetailsProps
) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [topic, setTopic] = useState('');
  const [partition, setPartition] = useState('');
  const [connection, setConnection] = useState('');
  const [disableSave, setDisableSave] = useState(false);
  const [egressFields, setEgressFields] = useState<IEgressDependencyFields>({ topic: '', partition: '', connection: '' });
  const [show, setShow] = useState(false);
  const [json, setJson] = useState({ payload: '', error: '' });
  const [viewSchemaClicked, setViewSchemaClicked] = useState(false);
  const { handleInputChange, formData, setFormParamValues } = useEgressFormValidation();
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [notFoundError, setNotFoundError] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [showJoltModal, setShowJoltModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEventLoading, setIsEventLoading] = useState(false);
  const [showAiAssistModal, setShowAiAssistModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [jsonInput, setJsonInput] = useState('');
  const [inputType, setInputType] = useState<'json' | 'curl'>('json');
  const [curlInput, setCurlInput] = useState('');

  const [searchListState, dispatchSearchListState] = useReducer(
    getIngressEventSearchListStateReducer,
    searchListInitialState
  );

  const { isEditService } = useSelector((state) => state.serviceOperationMonitorReducer);

  const { activeServiceEvent, isLoading } = useSelector(
    (state) => state.fetchEgressServiceEventReducer
  );

  const { egressEventMappingList } = useSelector(
    (state) => state.egressEventMappingReducer
  );

  const { eventDetails, isLoading: isSchemaLoading } = useSelector(
    (state) => state.getEventIdReducer
  );

  const HELPER_TEXT = {
    search_event_ht: translate('helper_text.txt_search_event_ht'),
    jolt_ht: translate('helper_text.txt_jolt_ht'),
    name_ht: translate('helper_text.txt_validJson_ht'),
    partition_id_ht: translate('helper_text.txt_partition_id_ht')
  };
  const { searchListData } = searchListState;
  const firstSearchData = searchListData[0];
  const searchedEventName = firstSearchData?.eventName;
  const searchedEventVersion = firstSearchData?.version;
  const isSearchDataNotEmpty = searchListData.length > 0;
  const isEditModeOff = checkEgressForEditMode(editEgressEventButtonClick, viewMode);
  const isIngressExist = isIngressExistForEgress(firstSearchData, ingressName, ingressEventVersion);
  const errorIngressCondition = isErrorIngressCondition(isSearchDataNotEmpty, isIngressExist);
  const errorEgressCondition = isEgressErrorCondition(searchListState.searchListData.length, egressEventMappingList, searchedEventName, serviceOperationName, ingressName, ingressEventVersion, searchedEventVersion?.toString()) && isEditModeOff;
  const errorCondition = isErrorCondition(searchListState.searchListData.length, egressEventMappingList, searchedEventName, serviceOperationName, ingressName, searchedEventVersion?.toString(), ingressEventVersion) && isEditModeOff;

  const isEventError = isEgressEventError(notFoundError, errorIngressCondition);
  const duplicateIngressErrorMsg = isEventError ? translate('service_event_association.lbl_duplicate_ingress_event') : '';
  const duplicateEgressErrorMsg = errorCondition ? (translate('service_event_association.lbl_duplicate_egress_event')) : duplicateIngressErrorMsg;
  const autoCompleteErrorMsg = egressErrorMessageHandler({
    notFoundError,
    notFoundErrorMessage: translate('service_event_association.lbl_no_match_found'),
    duplicateEgressErrorMsg,
    isIngressExist,
    duplicateIngressErrorMsg: translate('service_event_association.lbl_duplicate_egress_ingress_event')
  });

  const checkShouldUpdateEnable = (): boolean => {
    const editedEgressEvent = egressEventMappingList.find((event) => event.eventName === selectionForEdit && event.eventVersion === versionForEdit);
    if (editedEgressEvent) {
      const clonedEgressData = {
        resAttribute: editedEgressEvent?.resAttribute,
        mqResAttribute: editedEgressEvent?.mqResAttribute,
        topic: editedEgressEvent.topic ?? '',
        partition: editedEgressEvent.partition ?? '',
        connection: editedEgressEvent?.connection ?? ''
      };
      const currentFormData = {
        resAttribute: formData.responseAttribute.value,
        mqResAttribute: formData.mqResponseAttribute.value,
        topic: formData.topicName.value,
        partition: formData.partitionId.value,
        connection: formData.connectionString.value
      };
      return JSON.stringify(currentFormData) === JSON.stringify(clonedEgressData);
    }
    return false;
  };

  const getSchemaDetails = () => {
    setViewSchemaClicked(true);
    const schemaName = firstSearchData?.eventSchemaId;
    if (schemaName) {
      dispatch(fetchEventRequest(searchedEventName, searchedEventVersion));
    }
  };

  const handleShow = () => {
    getSchemaDetails();
    setShow(true);
  };

  const handleModalClose = () => {
    setShow(false);
    setJson({ payload: '', error: '' });
  };

  const topicHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentTopic = e.target.value;
    const updatedEgressFields: IEgressDependencyFields = { ...egressFields };
    updatedEgressFields.topic = currentTopic;
    handleInputChange(e);
    setTopic(currentTopic);
    setEgressFields(updatedEgressFields);
  };

  const partitionHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentPartition = e.target.value;
    const updatedEgressFields: IEgressDependencyFields = { ...egressFields };
    updatedEgressFields.partition = currentPartition;
    handleInputChange(e);
    setPartition(currentPartition);
    setEgressFields(updatedEgressFields);
  };

  const connectionHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentConnection = e.target.value;
    const updatedEgressFields: IEgressDependencyFields = { ...egressFields };
    updatedEgressFields.connection = currentConnection;
    handleInputChange(e);
    setConnection(currentConnection);
    setEgressFields(updatedEgressFields);
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLElement>) => {
    const notValid = ['e', 'E', '+', '-', '.'];
    return notValid.includes(e.key) && e.preventDefault();
  };

  /**
   * Performs the action of cancel add ingress event button clicked
   */
  const onCancelAddIngressEventClick = () => {
    onCancelButtonClicked();
  };

  /**
   * Handles the input change for search event and makes the api call to find an event
   */
  const handleChange = (value: string) => {
    setInputValue(value);
    dispatchSearchListState({
      type: INGRESS_EVENT_FORM_ACTIONS.SET_SEARCH_VALUE,
      searchValuePayload: { searchValue: value }
    });

    if (value.length > 2) {
      setTimeout(() => {
        dispatch(
          fetchServiceEventRequest(value, STATUS_TYPES.ACTIVE, associationEventType.EGRESS)
        );
        dispatchSearchListState({
          type: INGRESS_EVENT_FORM_ACTIONS.SET_SEARCH_LIST_VISIBLE,
          searchListVisiblePayload: { searchListVisible: true }
        });
      }, 200);
    } else {
      dispatchSearchListState({
        type: INGRESS_EVENT_FORM_ACTIONS.SET_SEARCH_LIST_VISIBLE,
        searchListVisiblePayload: { searchListVisible: false }
      });
      setSuggestions([]);
    }
  };

  /**
   * calls on event selected from searched event panel
   */
  const onSelectSearch = (val: string) => {
    setInputValue(val);
    const [eventName, , version] = val.split(' ');
    const versionNumber = +version.split(')')[0];
    setNotFoundError(false);
    const filterList = activeServiceEvent.filter(
      (item) => item.eventName === eventName && item.version === versionNumber
    );
    dispatchSearchListState({
      type: INGRESS_EVENT_FORM_ACTIONS.ON_SEARCH_VALUE_SELECT,
      searchListVisiblePayload: { searchListVisible: false },
      addSearchListPayload: { searchListData: filterList },
      searchValuePayload: { searchValue: val }
    });
    setSuggestions([]);
  };

  /**
   * Save Ingress EVent in the store
   */
  const saveEgressEvent = () => {
    const selectedEvent = firstSearchData;
    let egressEvent: IEgressEventMappingModel = {
      serviceOperationName,
      ingressName,
      ingressEventVersion,
      eventName: selectedEvent?.eventName,
      eventVersion: String(selectedEvent?.version),
      resAttribute: formData?.responseAttribute?.value,
      mqResAttribute: formData?.mqResponseAttribute?.value
    };
    egressEvent = topic ? { ...egressEvent, topic } : egressEvent;
    egressEvent = partition ? { ...egressEvent, partition } : egressEvent;
    egressEvent = connection ? { ...egressEvent, connection } : egressEvent;
    if (editEgressEventButtonClick) {
      const editIndex = egressEventMappingList.findIndex(
        (item) => item.eventName === selectionForEdit && item.ingressName === ingressName
          && item.serviceOperationName === serviceOperationName
          && item.ingressEventVersion === ingressEventVersion && item.eventVersion === versionForEdit
      );
      dispatch(
        updateEgressEventMapping({
          serviceOperationName,
          egressEventMapping: egressEvent,
          editIndex
        })
      );
    } else {
      dispatch(addEgressEventMapping({ egressEventMapping: egressEvent }));
    }
    onSaveEventButtonClick();
  };

  const shouldAddEvent = () => {
    let allowed = true;
    if (searchListState && isSearchDataNotEmpty && firstSearchData?.eventName) {
      const allChecks = [
        disableSave, // check if external topic has all fields
        viewMode, // check if in view mode
        !formData.responseAttribute.isValid, // responseAttribute should be valid
        !formData.partitionId.isValid,
        !formData.topicName.isValid,
        isIngressExist, // is it existing ingress?
        isEgressEventExist(egressEventMappingList, searchedEventName, serviceOperationName, searchedEventVersion?.toString(), ingressEventVersion, ingressName) && isEditModeOff// is it existing egress?
      ];
      allowed = allChecks.some((i) => i === true); // return true if any condition satisfies
    } else if (editEgressEventButtonClick) {
      const allChecksInEdit = [
        disableSave, // check if external topic has all fields
        viewMode, // check if in view mode
        !formData.responseAttribute.isValid // responseAttribute should be valid
      ];
      allowed = allChecksInEdit.some((i) => i === true); // return true if any condition satisfies
    }
    return allowed;
  };

  const validTopic = formData.topicName.isValid;
  const validPartitionId = formData.partitionId.isValid;
  const validConnection = formData.connectionString.isValid;

  const checkValidTopic = () => {
    if (validTopic && validPartitionId && validConnection) {
      setDisableSave(false);
    } else {
      setDisableSave(true);
    }
    return disableSave;
  };

  useEffect(() => {
    if (!topic && !partition && !connection) {
      const newFormData = { ...formData };
      newFormData.topicName.isTouched = false;
      newFormData.topicName.isValid = true;
      newFormData.partitionId.isTouched = false;
      newFormData.partitionId.isValid = true;
      newFormData.connectionString.isTouched = false;
      newFormData.connectionString.isValid = true;
      setFormParamValues(newFormData);
      setDisableSave(false);
    } else if (topic && (connection || partition)) {
      if (validTopic && (validPartitionId || validConnection)) {
        setDisableSave(false);
        return;
      }
      setDisableSave(true);
    } else if (topic) {
      checkValidTopic();
    } else {
      setDisableSave(true);
    }
  }, [topic, partition, connection]);

  const isSearchListValid = () => searchListState.searchListData.length > 0 || (editEgressEventButtonClick && searchListState.searchListData.length > 0);

  /**
   * Gets the edited event from store
   * @returns edited event
   */
  const findEditedEvent = () => egressEventMappingList.find(
    (item) => item.eventName === selectionForEdit && item.ingressName === ingressName
      && item.serviceOperationName === serviceOperationName
      && item.ingressEventVersion === ingressEventVersion
      && item.eventVersion === versionForEdit
  );

  /**
   * Update the form date based on the event details
   * @param editedEvent event being edited
   */
  const updateFormData = (editedEvent: IEgressEventMappingModel) => {
    const fieldsMapping = {
      connectionString: 'connection',
      partitionId: 'partition',
      topicName: 'topic',
      responseAttribute: 'resAttribute',
      mqResponseAttribute: 'mqResAttribute'
    };
    const newFormData = { ...formData };
    Object.entries(fieldsMapping).forEach(([formField, eventField]) => {
      const formKey = formField as keyof IEgressFormValidationModel;
      const eventKey = eventField as keyof IEgressEventMappingModel;
      newFormData[formKey].value = editedEvent[eventKey] ?? '';
      newFormData[formKey].isValid = true;
      newFormData[formKey].isTouched = true;
      newFormData[formKey].error = '';
    });
    setFormParamValues(newFormData);
  };

  /**
   * Update the event details
   * @param editedEvent event being edited
   */
  const updateEventDetails = (editedEvent: IEgressEventMappingModel) => {
    if (editedEvent.topic || editedEvent.partition || editedEvent.connection) {
      setTopic(editedEvent?.topic ?? '');
      setPartition(editedEvent?.partition ?? '');
      setConnection(editedEvent.connection ?? '');
    }
  };

  const dispatchActiveEvent = (editedEvent: IEgressEventMappingModel, eventData: IEvent[]) => {
    const { schema, eventType, eventDescription, eventName, } = eventData[0];
    const activeEvent: IActiveServiceEvent = {
      uuid: '',
      status: '',
      version: Number(editedEvent?.eventVersion),
      createdBy: '',
      createdTimeStamp: null,
      updatedBy: '',
      updatedTimeStamp: null,
      eventId: eventName,
      eventName,
      eventDescription,
      eventType,
      eventCategory: '',
      eventSchemaId: schema,
      eventSchemaVersion: 1,
      eventStatusHistory: []
    };
    dispatchSearchListState({
      type: INGRESS_EVENT_FORM_ACTIONS.SAVE_SEARCH_LIST_DATA,
      addSearchListPayload: { searchListData: [activeEvent] }
    });
  };

  const getEventDetails = async () => {
    // if edit event button was click then get data from store and set it to the local variables
    if (editEgressEventButtonClick || viewMode) {
      const editedEvent = findEditedEvent();
      if (editedEvent) {
        setIsEventLoading(true);
        const { eventData, isError } = await callGetServiceAssociatedEventApi(editedEvent?.eventName ?? '', editedEvent?.eventVersion ?? '');
        if (eventData?.length) {
          updateFormData(editedEvent);
          updateEventDetails(editedEvent);
          dispatchActiveEvent(editedEvent, eventData);
          setIsEventLoading(false);
        } else if (isError) {
          setShowModal(true);
          setIsEventLoading(false);
        }
      }
    }
  };

  const callEventDetailsApi = debounce(() => {
    getEventDetails();
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    // if edit event button was click then get data from store and set it to the local variables
    callEventDetailsApi();
  }, [editEgressEventButtonClick]);

  useEffect(() => {
    if (addBtnClick && !editEgressEventButtonClick) {
      const newFormData = { ...formData };
      const fields = ['connectionString', 'partitionId', 'topicName', 'responseAttribute', 'mqResponseAttribute'];
      fields.forEach((field) => {
        const key = field as keyof IEgressFormValidationModel;
        newFormData[key].value = '';
        newFormData[key].isValid = true;
        newFormData[key].isTouched = false;
        newFormData[key].error = '';
      });
      setFormParamValues(newFormData);
    }
  }, [addBtnClick]);

  useEffect(() => {
    setJson(getSchemaForEvent(isSchemaLoading, eventDetails[0].schema, viewSchemaClicked, json));
  }, [eventDetails, isSchemaLoading]);

  useEffect(() => {
    if (activeServiceEvent.length > 0) {
      const activeServiceEvents = activeServiceEvent.map((item) => `${item.eventName} (${VER} ${item.version})`);
      setSuggestions(activeServiceEvents);
      setNotFoundError(false);
    } else if (inputValue.length > 2 && !isLoading) {
      setSuggestions([]);
      setNotFoundError(true);
    }
  }, [activeServiceEvent]);

  const onFileUploadSuccess = (fileData: string) => {
    const newFormData = { ...formData };
    newFormData.mqResponseAttribute.value = fileData;
    setFormParamValues(newFormData);
  };

  const getEditViewEgressEventTitle = () => {
    if (getModeFromUrl() === SERVICE_MODE.VIEW) {
      return translate('service_event_form.lbl_view_egress_event');
    }
    if (editEgressEventButtonClick) {
      return translate('service_event_form.lbl_edit_egress_event');
    }
    return `${translate('service_event_form.lbl_add_egress_event')} ${translate('service_event_form.lbl_verbiage_for')} ${ingressName} - ${translate('service_event_form.lbl_verbiage_ver')} ${ingressEventVersion}`;
  };

  /**
   * Close the fetch event details fetch modal
   */
  const onModalClose = () => {
    setShowModal(false);
    onCancelButtonClicked();
  };

  /**
   * @method handleAiAssistModalClick
   * @description Opens the AI assist modal.
   */
  const handleAiAssistModalClick = () => {
    setShowAiAssistModal(true);
  };

  /**
   * @method handleCloseAiAssistModalDialog
   * @description Closes the AI assist modal and resets states.
   */
  const handleCloseAiAssistModalDialog = () => {
    setShowAiAssistModal(false);
    setSelectedFile(null);
    setFileName('');
    setJsonInput('');
    setCurlInput('');
    setInputType('json');
  };

  /**
   * @method handleInputTypeChange
   * @description Handles the input type change between JSON and cURL.
   */
  const handleInputTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputType(e.target.value as 'json' | 'curl');
    setJsonInput('');
    setCurlInput('');
  };

  /**
   * @method handleFileUploadChange
   * @description Handles file upload change.
   */
  const handleFileUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  /**
   * @method handleFileUploadClick
   * @description Triggers the file input click.
   */
  const handleFileUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Add headers function for authorization
  const headersInstance = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken.value}`,
  });

  /**
   * @method handleGenerateSchema
   * @description Handles the schema generation from uploaded file.
   */
  const handleGenerateSchema = async () => {
    console.log('handleGenerateSchema called'); // Debug log
    console.log('inputType:', inputType); // Debug log
    console.log('jsonInput:', jsonInput); // Debug log
    console.log('curlInput:', curlInput); // Debug log
    const inputData = inputType === 'json' ? jsonInput : curlInput;
    if (inputData.trim()) {
      try {
        let requestPayload;
        if (inputType === 'json') {
          // Validate that the input is valid JSON
          const parsedJson = JSON.parse(jsonInput);
          console.log('JSON is valid'); // Debug log
          // Convert the JSON input to the required format
          requestPayload = {
            outputjson: JSON.stringify(parsedJson)
          };
          console.log('Formatted JSON request payload:', requestPayload); // Debug log
        } else {
          // For cURL, pass the raw cURL command as string in outputjson field
          requestPayload = {
            outputjson: curlInput.trim()
          };
          console.log('Formatted cURL request payload:', requestPayload); // Debug log
        }
        // Get event name and version from the selected event details
        const eventName = searchListState.searchListData[0]?.eventName;
        const version = searchListState.searchListData[0]?.version;
        console.log('Event info from searchListData:', { eventName, version }); // Debug log
        if (!eventName || !version) {
          console.error('Event name or version not available from selected event details');
          console.error('searchListData:', searchListState.searchListData); // Debug log
          return;
        }
        // Make API call to generate JOLT mapping using POST
        const apiUrl = `${HELP_CENTER_CHATPAGE_SERVICE_BASE_URL}/generate-jolt-from-event?event_name=${eventName}&event_version=${version}&type=rs-attr`;
        console.log('Making API call to:', apiUrl); // Debug log
        console.log('Request payload:', requestPayload); // Debug log
        const response = await axios.post(apiUrl, requestPayload, {
          headers: headersInstance(),
        });
        console.log('API response:', response.data); // Debug log
        if (response.data) {
          // Extract jolt_spec from the response
          const joltSpec = response.data.jolt_spec;
          if (joltSpec) {
            // Update the JOLT mapping textarea using setFormParamValues
            setFormParamValues((previousState) => {
              const data = structuredClone(previousState);
              data.responseAttribute.isValid = true;
              data.responseAttribute.value = JSON.stringify(joltSpec, null, 2); // Pretty format the jolt_spec JSON
              data.responseAttribute.isTouched = true;
              data.responseAttribute.error = '';
              return data;
            });
            console.log('JOLT mapping updated with jolt_spec'); // Debug log
            // Close the modal
            handleCloseAiAssistModalDialog();
          } else {
            console.error('No jolt_spec found in response');
          }
        }
      } catch (error) {
        console.error('Error generating schema:', error);
        if (error instanceof SyntaxError && inputType === 'json') {
          alert('Invalid JSON format. Please check your input.');
        } else {
          alert('Error generating schema. Please try again.');
        }
      }
    }
  };

  const isAutoCompleteError = () => notFoundError || (!notFoundError && (errorIngressCondition || errorEgressCondition));

  const aiAssistModalBody = (
    <div className={classes['ai-assist-modal-body']}>
      <div className={classes['upload-section']}>
        <p className={classes['upload-description']}>
          Provide Sample Business Service Response Json to get AI assistance in generating JOLT spec.
        </p>
        <Textarea
          label="Sample Business Service Response JSON"
          name="jsonInput"
          value={jsonInput}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJsonInput(e.target.value)}
          style={{ height: '400px', minHeight: '400px', maxHeight: 'none' }}
          placeholder="Paste your sample business service response JSON here"
          maxLength="1048576" // 1MB limit - very high
        />
      </div>
    </div>
  );

  const shouldShowJoltConfig = () => (searchListState.searchListData.length > 0 || editEgressEventButtonClick) && !errorCondition;

  const EventDetails = () => (
    useMemo(() => (
      (isSearchListValid()) ? (
        <Row>
          <div className={classes['event-details-container']}>
            <div className={classes.details}>
              <div>{translate('service_event_form.lbl_event_name')}</div>
              <span className={classes['details-info']}>{searchedEventName}</span>
            </div>
            <div className={classes.details}>
              <div>{translate('service_event_form.lbl_event_version')}</div>
              <span className={classes['details-info']}>{searchedEventVersion}</span>
            </div>
            <div className={classes.details}>
              <div>{translate('service_event_form.lbl_event_type_form')}</div>
              <span className={classes['details-info']}>{firstSearchData?.eventType}</span>
            </div>
            <div className={classes.details}>
              <div>{translate('service_event_form.lbl_description')}</div>
              <span className={classes['details-info']}>{firstSearchData?.eventDescription}</span>
            </div>
            <div className={classes['view-schema']}>
              <span
                onClick={() => handleShow()}
                onKeyDown={() => { }}
                role="button"
                tabIndex={-1}
                className=""
              >
                {translate('service_event_form.lbl_view_schema')}
              </span>
            </div>
          </div>
        </Row>
      ) : null
    ), [])
  );

  return (
    <div>
      <Loader show={isEventLoading} />
      <Modal
        modalShow={showModal}
        modalTitle={translate('service_wrapper_component.lbl_confirm_error_title')}
        modalBody={translate('service_wrapper_component.msg_api_error')}
        btnSaveText={String(translate('service_wrapper_component.lbl_close'))}
        onSaveModal={onModalClose}
        onCloseModal={onModalClose}
        alignToCentre
        data-testid="activeStatusEditWarningModalNodeConfirm"
        disableCloseBtn={false}
        disableSaveBtn={false}
        type="warning"
      />
      <Form className={classes['event-form']}>
        <Row>
          <div className={classes['egress-title']}>
            <Button variant="primary" className={classes['back-btn']} onClick={onCancelAddIngressEventClick} aria-label="egress back button"><i className="bi bi-chevron-left" /></Button>
            <div>
              <div className={classes['egress-event-header']}>{getEditViewEgressEventTitle()}</div>
              <div className={classes['egress-event-desc']}>{translate('service_event_form.desc_egress_event')}</div>
            </div>
          </div>
        </Row>
        <Col className={classes['input-container']}>
          {
            !(viewMode || (isEditService && editEgressEventButtonClick))
            && (
              <Row className="mt-2">
                <Col sm={5}>
                  <div className={classes['search-input']}>
                    <AutoComplete
                      labelText={String(translate('service_event_form.lbl_search_existing_event'))}
                      suggestions={suggestions}
                      onSelect={onSelectSearch}
                      onqueryUpdate={handleChange}
                      isLoading={isLoading}
                      clearData
                      placeHolder={translate('event_list_component.plcd_lbl_search_event')}
                      isDisabled={viewMode || (isEditService && editEgressEventButtonClick)}
                      minSearchLength={3}
                      helperText={HELPER_TEXT.search_event_ht}
                      fieldName={AUTOCOMPLETE_FIELD.eventName}
                      error={isAutoCompleteError()}
                      errorTxt={autoCompleteErrorMsg}
                    />
                  </div>
                </Col>
              </Row>

            )
          }
          <div>{(searchListState.searchListData.length > 0
            || editEgressEventButtonClick) && !errorCondition && (
              <Row className={classes['event-details-header']}>
                {translate('service_event_form.lbl_event_details')}
              </Row>
            )}
            {!errorCondition && (<EventDetails />)}
            {shouldShowJoltConfig() && (
              <>
                <Row className={classes['event-details-header']}>
                  <Col>
                    <CILabel id="responseAttribute" labelText={translate('service_event_form.lbl_response_attribute_mapping')} />
                  </Col>
                  <Col className={classes['jolt-config-beta-link']}>
                    <div className={classes['button-group']}>
                      <Button
                        variant="tertiary"
                        className={classes['ai-assist-btn']}
                        onClick={handleAiAssistModalClick}
                        disabled={viewMode}
                        data-testid="ai-assist-btn"
                      >
                        <small>{translate('service_event_form.ai_assist')}</small>
                        <CIInfoIcon iconTxt={String(translate('service_event_form.ai_assist_info_bubble'))} />
                      </Button>
                      <span className={classes['btn-beta-text']}>{translate('schema_details_form_components.beta_title')}</span>
                    </div>
                    <div className={classes['button-group']}>
                      <Button
                        variant="tertiary"
                        className={classes['configure-jolt-btn']}
                        onClick={() => setShowJoltModal(true)}
                      >
                        <small>{translate('service_event_form.configure_jolt_btn')}</small>
                      </Button>
                      <span className={classes['btn-beta-text']}>{translate('schema_details_form_components.beta_title')}</span>
                    </div>
                  </Col>
                </Row>
                <Row className={classes['text-area']}>
                  <Textarea
                    placeholder={String(translate('event_list_component.txt_add_jolt'))}
                    name="responseAttribute"
                    id="responseAttribute"
                    label=""
                    value={formData.responseAttribute.value}
                    onChange={handleInputChange}
                    data-testid="egress-response-attr"
                    maxLength=""
                    disabled={viewMode}
                    helperText={HELPER_TEXT.jolt_ht}
                    error={!formData.responseAttribute.isValid}
                    errorTxt={formData.responseAttribute.error}
                  />
                </Row>
                {operationVerb === OPERATION_VERB[10].name && (
                  <Row className={classes['event-details-header']}>
                    <div className={classes['copybook-container']}>
                      {translate(
                        'service_event_form.lbl_mq_message_attribute_mapping'
                      )}

                      <FileUploadButton viewMode={viewMode ?? false} onFileUploadSuccess={onFileUploadSuccess} />
                    </div>
                  </Row>
                )}
                {operationVerb === OPERATION_VERB[10].name && (
                  <Row className={classes['text-area']}>
                    <Textarea
                      placeholder={translate(
                        'service_event_form.lbl_mq_message_attribute_mapping'
                      ) ?? ''}
                      name="mqResponseMapping"
                      label=""
                      value={formData.mqResponseAttribute.value}
                      onChange={handleInputChange}
                      maxLength=""
                      disabled
                      data-testid="mqResponseMapping"
                    />
                    <div className={classes['error-container']} />
                  </Row>
                )}
                <Row className={classes['event-details-header']}>
                  <Col sm={8}>
                    <Input
                      isMandatory={!!partition?.length || !!connection?.length}
                      labelText={String(translate('service_event_form.lbl_egress_topic_name'))}
                      label=""
                      name="topicName"
                      placeholder={String(translate('event_list_component.txt_topic_name'))}
                      value={formData.topicName.value}
                      onChange={topicHandler}
                      data-testid="egress-topic"
                      disabled={viewMode}
                      error={!formData.topicName.isValid}
                      helperText={HELPER_TEXT.name_ht}
                      errorTxt={formData.topicName.error}
                    />
                  </Col>
                  <Col sm={4}>
                    <Input
                      labelText={String(translate('service_event_form.lbl_egress_partition_id'))}
                      label=""
                      name="partitionId"
                      onKeyDown={onKeyDownHandler}
                      placeholder={String(translate('event_list_component.txt_partition_id'))}
                      value={formData.partitionId.value}
                      onChange={partitionHandler}
                      data-testid="egress-partition"
                      disabled={viewMode}
                      error={!formData.partitionId.isValid}
                      helperText={HELPER_TEXT.partition_id_ht}
                      errorTxt={formData.partitionId.error}
                    />
                  </Col>
                </Row>
                <Row className={classes['event-details-header']}>
                  <Input
                    labelText={String(translate('service_event_form.lbl_egress_connection_string'))}
                    label=""
                    name="connectionString"
                    placeholder={String(translate('event_list_component.txt_connection_string'))}
                    value={formData.connectionString.value}
                    onChange={connectionHandler}
                    data-testid="egress-connection"
                    disabled={viewMode}
                    error={!formData.connectionString.isValid}
                    errorTxt={formData.connectionString.error}
                  />
                </Row>

              </>
            )}
          </div>
        </Col>
        <div className={`${classes['btn-container']} ci-button-group`}>
          <Button
            variant="secondary"
            onClick={onCancelAddIngressEventClick}
            data-testid="egress-cancel-btn"
          >
            {translate('service_wrapper_component.lbl_cancel')}
          </Button>
          <Button
            variant="primary"
            className={classes['btn-add']}
            disabled={shouldAddEvent() || (checkShouldUpdateEnable() && editEgressEventButtonClick)}
            onClick={saveEgressEvent}
            data-testid="egress-save-btn"
          >
            {(isEditService || editEgressEventButtonClick) ? (translate('service_wrapper_component.lbl_save_egress')) : (translate('service_wrapper_component.lbl_add_egress'))}
          </Button>
        </div>
      </Form>
      <EventSchemaTree show={show} closeModal={handleModalClose} json={json} getSchemaIsLoading={isSchemaLoading} />
      <Modal modalTitle={translate('event_list_component.txt_jolt_config')} modalBody={<JoltConfigurations />} className="modal-xl" modalShow={showJoltModal} onCloseModal={() => setShowJoltModal(false)} />
      <Modal
        modalTitle={translate('service_event_form.ai_assist_modal_title')}
        btnCloseText={String(translate('service_event_form.ai_assist_modal_cancel'))}
        btnSaveText={String(translate('service_event_form.ai_assist_modal_generate_button'))}
        modalBody={aiAssistModalBody}
        modalShow={showAiAssistModal}
        onCloseModal={handleCloseAiAssistModalDialog}
        onSaveModal={handleGenerateSchema}
        alignToCentre
        disableSaveBtn={inputType === 'json' ? !jsonInput.trim() : !curlInput.trim()}
        disableCloseBtn={false}
        className={classes['ai-assist-modal']}
      />
    </div>
  );
};
EgressEventAssociationForm.defaultProps = defaultProps;
export default EgressEventAssociationForm;
